import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Copy, Check, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';

/**
 * AddressModal — confirms the recipient delivery address before sending the
 * gifter to a partner store (Amazon etc.).
 *
 * Accepts BOTH legacy (`open` + `registry`) and new (`isOpen` + `deliveryAddress`)
 * prop shapes so the rewrite doesn't break in-flight call sites.
 */
const AddressModal = (props) => {
  const open = props.open ?? props.isOpen ?? false;
  const onClose = props.onClose;
  const onProceed = props.onProceed;
  const registry = props.registry;
  const deliveryAddress =
    props.deliveryAddress ||
    registry?.delivery_address ||
    'No delivery address provided yet.';

  const recipient =
    registry?.event_details?.mother_name ||
    registry?.event_details?.partner_names ||
    registry?.user_name ||
    'The registry owner';

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(deliveryAddress);
    setCopied(true);
    toast.success('Address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleProceed = () => {
    onProceed?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden">
        <div className="px-7 pt-8 pb-3 text-center">
          <LogoMark size={26} className="text-clay-700 mx-auto mb-4" />
          <DialogHeader>
            <DialogTitle className="font-display text-[26px] leading-tight text-ink">
              Sending it to the right doorstep.
            </DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm text-ink-soft">
            Copy this address — you'll paste it into the partner store at checkout.
          </p>
        </div>

        <div className="px-7 pb-8 pt-3 space-y-5">
          <div className="rounded-2xl bg-cream-100 border border-border p-4">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink-muted mb-1">
              Delivering to
            </p>
            <p className="font-display text-lg text-ink mb-3">{recipient}</p>
            <div className="rounded-xl bg-cream-50 border border-border p-3.5">
              <p className="text-[13.5px] text-ink-soft leading-relaxed whitespace-pre-line">
                {deliveryAddress}
              </p>
            </div>
          </div>

          <BrandButton variant="outline" onClick={handleCopy} className="w-full">
            {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy address</>}
          </BrandButton>

          <ol className="text-[13px] text-ink-soft space-y-1.5 pl-5 list-decimal">
            <li>Tap "Open partner store" below.</li>
            <li>Add the gift to your cart and continue to checkout.</li>
            <li>Paste this address as the delivery address.</li>
            <li>Come back and mark it gifted, so we can keep things tidy.</li>
          </ol>

          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <BrandButton variant="outline" onClick={onClose} className="sm:flex-1">
              Cancel
            </BrandButton>
            <BrandButton onClick={handleProceed} className="sm:flex-1">
              Open partner store
              <ArrowUpRight className="h-4 w-4" />
            </BrandButton>
          </div>

          <p className="text-xs text-ink-muted text-center">
            Keep this window open until you've pasted the address.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddressModal;
